import React, { useCallback, type ReactNode } from 'react';

import { cssMap } from '@compiled/react';
import { token } from '@atlaskit/tokens';

import ButtonGroup from '@atlaskit/button/button-group';
import Button from '@atlaskit/button/new';
import Form, { FormFooter, FormHeader } from '@atlaskit/form';

import LinkIcon from '@atlaskit/icon/core/link';

import { Text, Inline, Stack } from '@atlaskit/primitives/compiled';
import { useIntl } from '@atlassian/jira-intl';

import { useRouterActions } from '@atlassian/react-resource-router';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { type TypeDefinition, lookupTypeByTypeId } from '../../../../common/application-types.tsx';
import ProductCard from '../../../common/product-card/index.tsx';
import {
	autoConfigureApplinkConsumer,
	getOAuth2ConfigInfo,
	putApplink,
	putApplinkOAuthOne2LOProvider,
	putApplinkOAuthOne3LOProvider,
	createOAuth2Applink,
	fetchApplink,
} from '../../../../rest/index.tsx';
import type {
	ApplicationLinkCreationEntity,
	ApplicationLinkManifest,
} from '../../../../common/entities.tsx';
import {
	STATUS_RECIPROCAL_SUCCESS,
	generateApplinksCreationRedirectionUrl,
	updateStatusLog,
	type CreationState,
} from '../../../../common/interchange.tsx';
import type {
	StageProps,
	StageReciprocalReviewProps,
	StageReviewProps,
} from '../../../../common/types.tsx';
import commonMessages from '../../../messages.tsx';
import messages from '../messages.tsx';
import { THIS_PRODUCT_NAME_STRING } from '../../../../common/constants.tsx';
import { StatusLozenge } from '../../../common/status/lozenge/StatusLozenge.tsx';
import { ERRORS } from '../../../../common/status/errors.tsx';

const styles = cssMap({
	linkDisplay: {
		marginTop: token('space.200'),
		marginBottom: token('space.200'),
	},
});

type ReviewSubpageProps = {
	state: StageReviewProps | StageReciprocalReviewProps;
	onSuccess: (newState: StageProps) => void;
	onError: (error: string) => void;
	onCancel: () => void;
};

const ReviewSubpage = (props: ReviewSubpageProps) => {
	const { formatMessage } = useIntl();

	const { push } = useRouterActions();

	const reciprocalMode = props.state.kind === 'reciprocal-review';

	const thisApplicationManifest: ApplicationLinkManifest = props.state.localManifest;
	const targetApplicationManifest: ApplicationLinkManifest = props.state.remoteManifest;

	// TODO: Determine if the type is DC or Cloud
	const thisApplicationType: TypeDefinition = lookupTypeByTypeId(
		thisApplicationManifest.typeId,
		false,
	);
	const targetApplicationType: TypeDefinition = lookupTypeByTypeId(
		targetApplicationManifest.typeId,
		false,
	);

	const onFormSubmitOAuth1 = async () => {
		const applinkCreationEntity: ApplicationLinkCreationEntity = {
			id: targetApplicationManifest.id,
			name: targetApplicationManifest.name,
			displayUrl: targetApplicationManifest.url,
			rpcUrl: targetApplicationManifest.url,
			typeId: targetApplicationManifest.typeId,
			isPrimary: false,
			isSystem: false,
			isOAuth2ThreeLoEnabled: false,
		};

		try {
			await putApplink(applinkCreationEntity);

			if (
				props.state.kind === 'reciprocal-review' &&
				props.state.inboundCreationState.applinkOriginalCreatedId
			) {
				// Configure OAuth
				await putApplinkOAuthOne3LOProvider(targetApplicationManifest.id);
				await autoConfigureApplinkConsumer(targetApplicationManifest.id);
				await putApplinkOAuthOne2LOProvider(targetApplicationManifest.id);

				// Update status log
				const updatedApplinkCreationStatusLog = updateStatusLog(
					props.state.inboundCreationState.applinkCreationStatusLog,
					{ status: STATUS_RECIPROCAL_SUCCESS },
				);

				const newCreationState: CreationState = {
					applinkCreationStatusLog: updatedApplinkCreationStatusLog,
					applinkOriginalCreatedId: props.state.inboundCreationState.applinkOriginalCreatedId,
					sharedUserbase: props.state.inboundCreationState.sharedUserbase,
					applinkConfigInPreAuth: true,
					applinkConfigInServerAuth: true,
					applinkConfigInUserAuth: true,
					applinkConfigOutPreAuth: true,
					applinkConfigOutServerAuth: true,
					applinkConfigOutUserAuth: true,
				};

				const newRedirectionUrl = generateApplinksCreationRedirectionUrl(
					targetApplicationManifest.url,
					targetApplicationManifest.typeId,
					newCreationState,
				);

				push(newRedirectionUrl);
				return;
			}

			props.onSuccess({
				kind: 'reciprocal-prompt',
				variant: props.state.variant,
				remoteManifest: targetApplicationManifest,
				localManifest: thisApplicationManifest,
			});
		} catch (err) {
			const maybeError = err instanceof Error ? err : undefined;
			log.safeErrorWithoutCustomerData(
				'applinks.administration.list.creation-flow-dialog.review.submitOAuth1ReviewForm',
				'Failed to create an OAuth 1.0 link after reviewing',
				maybeError,
			);
			props.onError(
				maybeError ? maybeError.message : formatMessage(commonMessages.unknownErrorText),
			);
			return err;
		}
	};

	const onFormSubmitOAuth2 = async () => {
		const applinkCreationEntity: ApplicationLinkCreationEntity = {
			id: targetApplicationManifest.id,
			name: targetApplicationManifest.name,
			displayUrl: targetApplicationManifest.url,
			rpcUrl: targetApplicationManifest.url,
			typeId: targetApplicationManifest.typeId,
			isPrimary: false,
			isSystem: false,
			isOAuth2ThreeLoEnabled: true,
		};

		try {
			await createOAuth2Applink(applinkCreationEntity);

			const oauth2ConfigInfo = await getOAuth2ConfigInfo(targetApplicationManifest.id);
			const newlyCreatedApplink = await fetchApplink(targetApplicationManifest.id);

			props.onSuccess({
				kind: 'connect',
				variant: props.state.variant,
				applink: newlyCreatedApplink,
				oauth2ConfigInfo,
			});
		} catch (err) {
			const maybeError = err instanceof Error ? err : undefined;
			log.safeErrorWithoutCustomerData(
				'applinks.administration.list.creation-flow-dialog.review.submitOAuth2ReviewForm',
				'Failed to create an OAuth 2.0 link after reviewing',
				maybeError,
			);
			props.onError(
				maybeError ? maybeError.message : formatMessage(commonMessages.unknownErrorText),
			);
			return err;
		}
	};

	const onFormSubmit = async () => {
		switch (props.state.variant) {
			case 'OAUTH1.0':
			case 'TUNNEL':
				return onFormSubmitOAuth1();
			case 'OAUTH2.0':
				return onFormSubmitOAuth2();
			default:
				props.onError(formatMessage(messages.unknownConnectionTypeError));
		}
	};

	const getVariantProperName = useCallback(() => {
		switch (props.state.variant) {
			case 'OAUTH1.0':
				return formatMessage(messages.reviewOAuth1HeaderNonfinal);
			case 'OAUTH2.0':
				return formatMessage(messages.reviewOAuth2Header);
			case 'TUNNEL':
			default:
				return formatMessage(messages.reviewTunneledHeaderNonfinal);
		}
	}, [formatMessage, props.state.variant]);

	return (
		<Form onSubmit={onFormSubmit}>
			{({ formProps, submitting }) => (
				<form {...formProps}>
					<FormHeader
						title={
							reciprocalMode
								? formatMessage(messages.reviewIncomingLinkHeaderNonfinal)
								: getVariantProperName()
						}
					>
						{!reciprocalMode &&
							(props.state.variant === 'OAUTH1.0' || props.state.variant === 'TUNNEL') && (
								<Text>
									{formatMessage(messages.reviewOAuth1DescriptionNonfinal, {
										nameOfThisProduct: THIS_PRODUCT_NAME_STRING,
										nameOfRemoteProduct: targetApplicationType.name,
										strong: (chunks: ReactNode[]) => <Text as="strong">{chunks}</Text>,
									})}
								</Text>
							)}
						{!reciprocalMode && props.state.variant === 'OAUTH2.0' && (
							<Text>
								{formatMessage(messages.reviewOAuth2Description, {
									nameOfThisProduct: THIS_PRODUCT_NAME_STRING,
									nameOfRemoteProduct: targetApplicationType.name,
									strong: (chunks: ReactNode[]) => <Text as="strong">{chunks}</Text>,
								})}
							</Text>
						)}
						{reciprocalMode && (
							<Text>
								{formatMessage(messages.reviewOAuth1ReciprocalDescriptionNonfinal, {
									nameOfThisProduct: THIS_PRODUCT_NAME_STRING,
									nameOfRemoteProduct: targetApplicationType.name,
									strong: (chunks: ReactNode[]) => <Text as="strong">{chunks}</Text>,
								})}
							</Text>
						)}
					</FormHeader>

					<Stack xcss={styles.linkDisplay} space="space.200">
						<ProductCard
							applicationType={thisApplicationType}
							displayUrl={thisApplicationManifest.url}
							heading={formatMessage(messages.localProductCardHeader)}
						/>
						<Inline space="space.100" alignBlock="center" alignInline="center">
							<LinkIcon label="" />
							{reciprocalMode && <StatusLozenge kind="error" err={ERRORS.CONFIGURATION_COMPLETE} />}
							{!reciprocalMode && <StatusLozenge kind="not-connected" />}
						</Inline>
						<ProductCard
							applicationType={targetApplicationType}
							displayUrl={targetApplicationManifest.url}
							heading={formatMessage(messages.remoteProductCardHeader)}
						/>
					</Stack>

					<FormFooter>
						<ButtonGroup>
							<Button appearance="subtle" onClick={props.onCancel}>
								{formatMessage(messages.formCancelButtonText)}
							</Button>
							<Button
								testId="applinks-administration.ui.list.creation-flow-dialog.review.submit-button"
								type="submit"
								appearance="primary"
								isLoading={submitting}
							>
								{reciprocalMode
									? formatMessage(messages.reviewFormCompleteLinkButtonNonfinal)
									: formatMessage(messages.reviewFormCreateLinkButton)}
							</Button>
						</ButtonGroup>
					</FormFooter>
				</form>
			)}
		</Form>
	);
};

export default ReviewSubpage;
